import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

interface ImageProcessingDialogProps {
  isOpen: boolean;
  totalImages: number;
  currentImageIndex: number;
  extractedMessages: number;
}

// Time estimate in milliseconds per image
const ESTIMATED_TIME_PER_IMAGE = 4000;

export function ImageProcessingDialog({
  isOpen,
  totalImages,
  currentImageIndex,
  extractedMessages,
}: ImageProcessingDialogProps) {
  // Track smooth progress percentage
  const [smoothProgress, setSmoothProgress] = useState(0);

  // Track when current image started processing
  const lastImageStartTimeRef = useRef(Date.now());
  const animationFrameRef = useRef<number | null>(null);
  const prevImageIndexRef = useRef(0);
  const prevTotalImagesRef = useRef(0);
  const wasOpenRef = useRef(isOpen);
  const lastProcessedIndexRef = useRef(0);

  // Calculate progress thresholds
  const completedImages = Math.max(0, currentImageIndex - 1);
  const baseProgress =
    totalImages > 0 ? (completedImages / totalImages) * 100 : 0;
  const maxProgressForCurrentImage =
    totalImages > 0 ? ((completedImages + 1) / totalImages) * 100 : 0;

  // Reset progress when dialog closes
  useEffect(() => {
    // Check if dialog went from open to closed
    if (wasOpenRef.current && !isOpen) {
      // Reset everything
      setSmoothProgress(0);
      prevImageIndexRef.current = 0;
      prevTotalImagesRef.current = 0;
      lastProcessedIndexRef.current = 0;

      // Cancel any ongoing animations
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    // Update ref for next comparison
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Initialize when dialog opens or total images changes
  useEffect(() => {
    if (isOpen && totalImages > 0) {
      // Check if this is a new session or total images changed
      if (totalImages !== prevTotalImagesRef.current) {
        prevTotalImagesRef.current = totalImages;

        // If this is the first image, set the start time
        if (currentImageIndex === 1 && prevImageIndexRef.current === 0) {
          lastImageStartTimeRef.current = Date.now();
          prevImageIndexRef.current = 1;
          lastProcessedIndexRef.current = 0;
        }
      }
    }
  }, [isOpen, totalImages, currentImageIndex]);

  // Handle image index changes - this indicates a new image has started processing
  useEffect(() => {
    if (!isOpen) return;

    // If current image index changed
    if (currentImageIndex !== prevImageIndexRef.current) {
      // Update to the full progress of the previous image
      if (
        prevImageIndexRef.current > 0 &&
        prevImageIndexRef.current < totalImages
      ) {
        const completedProgress =
          (prevImageIndexRef.current / totalImages) * 100;
        setSmoothProgress(completedProgress);
      }

      // Record the start time for the new image
      lastImageStartTimeRef.current = Date.now();
      prevImageIndexRef.current = currentImageIndex;
    }

    // Cleanup animation frame on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, currentImageIndex, totalImages]);

  // Detect when the current image finishes processing (based on image index changes)
  useEffect(() => {
    if (!isOpen || totalImages === 0 || currentImageIndex === 0) return;

    // If we have a new image index and it's greater than what we've already processed
    // This means the current image finished processing before the animation completed
    if (currentImageIndex > lastProcessedIndexRef.current + 1) {
      // Cancel any ongoing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Update progress to include all finished images
      const completedProgress = ((currentImageIndex - 1) / totalImages) * 100;
      setSmoothProgress(completedProgress);

      // Update the last processed index
      lastProcessedIndexRef.current = currentImageIndex - 1;
    }
  }, [isOpen, currentImageIndex, totalImages]);

  // Handle smooth progress animation
  useEffect(() => {
    if (!isOpen || totalImages === 0 || currentImageIndex === 0) return;

    const updateProgress = () => {
      // If the current image is done, jump to its full progress
      if (currentImageIndex > completedImages + 1) {
        // Calculate the progress for completed images
        const completedProgress = ((completedImages + 1) / totalImages) * 100;
        setSmoothProgress(completedProgress);
      } else {
        // Calculate elapsed time for current image
        const elapsed = Date.now() - lastImageStartTimeRef.current;
        const imageProgress = Math.min(elapsed / ESTIMATED_TIME_PER_IMAGE, 1);

        // Calculate current progress
        const percentPerImage = 100 / totalImages;
        const adjustedProgress = baseProgress + imageProgress * percentPerImage;

        // Cap progress at the next checkpoint
        const cappedProgress = Math.min(
          adjustedProgress,
          maxProgressForCurrentImage
        );

        // Update progress
        setSmoothProgress(cappedProgress);

        // Continue animation if not at max for current image
        if (cappedProgress < maxProgressForCurrentImage) {
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        }
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(updateProgress);

    // Cleanup animation frame on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    isOpen,
    baseProgress,
    totalImages,
    maxProgressForCurrentImage,
    currentImageIndex,
    completedImages,
  ]);

  // Format for display
  const displayProgress = Math.min(Math.round(smoothProgress), 99);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Processing Images</DialogTitle>
          <DialogDescription>
            Extracting messages from your screenshots
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <div className="flex flex-col space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                Processing image {currentImageIndex} of {totalImages}
              </span>
              <span>{displayProgress}%</span>
            </div>
            <Progress value={displayProgress} className="w-full h-2" />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500">Images</p>
              <p className="text-lg font-medium">{totalImages}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500">Messages Found</p>
              <p className="text-lg font-medium">{extractedMessages}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
